import moment from "moment";

/**
 * Formats a date into a specified string format.
 * @param {any} date - The date value to format (can be a string, Date object, or moment-compatible value).
 * @param {string} format - The moment.js format string (e.g., "YYYY-MM-DD", "MM/DD/YYYY").
 * @param {string} [fallback=""] - The fallback value to return if the date is invalid or falsy.
 * @returns {string} The formatted date string, or the fallback value if the date is invalid.
 * @example
 * formatDate("2024-01-15", "MM/DD/YYYY") // Returns "01/15/2024"
 * formatDate(null, "YYYY-MM-DD", "N/A") // Returns "N/A"
 */
export const formatDate = (date: any, format: string, fallback: string = "") =>
  date && moment(date).isValid() ? moment(date).format(format) : fallback;

/**
 * Formats an ISO or calendar date string for coverage UI display (MM/DD/YYYY).
 *
 * @param iso - Date string in YYYY-MM-DD or ISO-8601 form, or empty when unknown.
 * @returns Formatted date, the original string if parsing fails, or an em dash when empty.
 */
export const formatDisplayDate = (iso: string): string => {
  if (!iso) return "—";
  const m = moment(iso, ["YYYY-MM-DD", moment.ISO_8601], true);
  if (!m.isValid()) return iso;
  return m.format("MM/DD/YYYY");
};

/**
 * Checks if two date strings represent the same calendar day.
 *
 * Comparison is performed in UTC so the result is independent of the
 * viewer's local timezone. Inputs may be plain calendar dates
 * (e.g. "YYYY-MM-DD", "MM/DD/YYYY") or full ISO-8601 timestamps;
 * in all cases only the year/month/day components are compared.
 *
 * @param {string} [date1] - The first date string to compare.
 * @param {string} [date2] - The second date string to compare.
 * @returns {boolean} True if both dates are valid and fall on the same UTC calendar day, false otherwise.
 * @example
 * checkIfDatesAreSame("2024-01-15", "2024-01-15") // Returns true
 * checkIfDatesAreSame("2024-01-15", "2024-01-16") // Returns false
 * checkIfDatesAreSame(undefined, "2024-01-15") // Returns false
 * checkIfDatesAreSame("2024-01-15T23:00:00Z", "2024-01-15") // Returns true regardless of local TZ
 */
export const checkIfDatesAreSame = (date1?: string, date2?: string) => {
  const m1 = moment.utc(
    date1,
    ["YYYY-MM-DD", "MM/DD/YYYY", moment.ISO_8601],
    false,
  );
  const m2 = moment.utc(
    date2,
    ["YYYY-MM-DD", "MM/DD/YYYY", moment.ISO_8601],
    false,
  );
  if (!m1.isValid() || !m2.isValid()) {
    return false;
  }
  return m1.isSame(m2, "day");
};

/**
 * Parses a date string in MM/DD/YYYY format into a JavaScript Date object.
 *
 * @param dateString - The date string to parse in MM/DD/YYYY format (e.g., "01/15/2024")
 * @returns A Date object representing the parsed date
 *
 * @example
 * parseMMDDYYYY("01/15/2024") // Returns Date object for January 15, 2024
 * parseMMDDYYYY("12/31/2023") // Returns Date object for December 31, 2023
 *
 * @remarks
 * This function assumes the input string is properly formatted.
 * Invalid or malformed strings may result in an Invalid Date.
 */
export const parseMMDDYYYY = (dateString: string): Date => {
  const [month, day, year] = dateString.split("/").map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Formats a date string to UTC and returns it in the specified format.
 * @param {string} date - The date string to format.
 * @param {string} format - The moment.js format string (e.g., "YYYY-MM-DD", "MM/DD/YYYY HH:mm:ss").
 * @param {string} [fallback=""] - The fallback value to return if the date is invalid.
 * @returns {string} The formatted UTC date string, or the fallback value if the date is invalid.
 * @example
 * formatDateToUTCWithFormat("2024-01-15T10:30:00-05:00", "YYYY-MM-DD HH:mm:ss") // Returns "2024-01-15 15:30:00" (UTC)
 * formatDateToUTCWithFormat("invalid-date", "YYYY-MM-DD", "N/A") // Returns "N/A"
 */
export const formatDateToUTCWithFormat = (
  date: string,
  format: string,
  fallback: string = "",
): string => {
  if (!date) return fallback;
  return moment(date).isValid() ? moment.utc(date).format(format) : fallback;
};
